import React, { useState, useEffect } from 'react';
import api from '../api';

export default function WorkOrders() {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState('');
  const [form, setForm] = useState({ title: '', client_id: '', event_date: '', scheduling_date: '', category: 'Festival', platforms: [], notes: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const PLATFORMS  = ['Instagram','Facebook','LinkedIn','YouTube','Twitter','Google Ads'];
  const CATEGORIES = ['Festival','Product Launch','Brand Campaign','Offer/Sale','Event','Awareness','Other'];

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [woRes, cRes] = await Promise.all([api.get('/work-orders'), api.get('/clients')]);
      setWorkOrders(woRes.data.data);
      setClients(cRes.data.data);
    } catch {} finally { setLoading(false); }
  };

  const togglePlatform = (p) => setForm(f => ({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.title)     { setFormError('Title is required'); return; }
    if (!form.client_id) { setFormError('Please select a client'); return; }
    if (!form.event_date){ setFormError('Event date is required'); return; }
    setSaving(true);
    try {
      await api.post('/work-orders', form);
      setShowForm(false);
      setForm({ title:'', client_id:'', event_date:'', scheduling_date:'', category:'Festival', platforms:[], notes:'' });
      loadData();
    } catch (err) { setFormError(err.response?.data?.message || 'Failed to create work order.'); }
    finally { setSaving(false); }
  };

  const filtered = workOrders.filter(wo =>
    !filter || wo.title.toLowerCase().includes(filter.toLowerCase()) || wo.client?.name?.toLowerCase().includes(filter.toLowerCase())
  );

  const statusBadge = (s) => {
    if (s === 'Closed')      return 'badge-green';
    if (s === 'Monitoring')  return 'badge-blue';
    if (s === 'In Progress') return 'badge-purple';
    if (s === 'Rework')      return 'badge-orange';
    return 'badge-gray';
  };

  if (loading) return <div className="page-wrap"><div className="empty-state"><div className="empty-state-icon">⏳</div><div className="empty-state-title">Loading...</div></div></div>;

  return (
    <div className="page-wrap">
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:8 }}>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ New Work Order</button>
      </div>

      <input className="form-input mb-16" placeholder="🔍 Search by title or client..." value={filter} onChange={e => setFilter(e.target.value)} style={{ marginBottom: '16px' }} />

      {filtered.length === 0 ? (
        <div className="empty-state card">
          <div className="empty-state-icon">📋</div>
          <div className="empty-state-title">No work orders yet</div>
          <div className="text-sm text-muted">Click "+ New Work Order" to create your first one.</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>WO Number</th>
                <th>Title</th>
                <th>Client</th>
                <th>Event Date</th>
                <th>Category</th>
                <th>Platforms</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(wo => (
                <tr key={wo.id}>
                  <td><span className="mono fw-700" style={{ color: 'var(--accent)' }}>{wo.wo_number}</span></td>
                  <td><span className="fw-600">{wo.title}</span></td>
                  <td>{wo.client?.name}</td>
                  <td><span className="mono">{wo.event_date}</span></td>
                  <td>{wo.category}</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {(wo.platforms || []).map(p => <span key={p} className="badge badge-gray">{p}</span>)}
                    </div>
                  </td>
                  <td><span className={`badge ${statusBadge(wo.cached_status)}`}>{wo.cached_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-header">
              <div className="modal-title">+ New Work Order</div>
              <button className="modal-close" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="modal-body">
              {formError && <div className="action-msg-error">{formError}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Title *</label>
                    <input className="form-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Guru Purnima" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Client *</label>
                    <select className="form-select" value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})}>
                      <option value="">Select client...</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Event Date *</label>
                    <input className="form-input" type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Scheduling Date</label>
                    <input className="form-input" type="date" value={form.scheduling_date} onChange={e => setForm({...form, scheduling_date: e.target.value})} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Platforms</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {PLATFORMS.map(p => (
                      <button type="button" key={p}
                        className={`btn ${form.platforms.includes(p) ? 'btn-primary' : 'btn-secondary'}`}
                        style={{ fontSize: '0.78rem', padding: '5px 12px' }}
                        onClick={() => togglePlatform(p)}
                      >{p}</button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Any notes..." />
                </div>
                <div className="modal-footer" style={{ padding: '0', border: 'none', marginTop: '8px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create Work Order'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
